import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { fetchWithAuth } from "../../../api-client/api_client";
import { Integration, LinkedAccount } from "../../../models/Entities";
import { MappingTestSuiteMeta } from "../../../models/MappingTests";
import { navigateToIndividualTestSuite } from "../../../router/RouterUtils";
import VersionControlSaveModal from "../../blueprint-editor/control-panel/VersionControlSaveModal";
import DropdownFormField from "../../blueprint-editor/right-panel/DropdownFormField";
import { showErrorToast } from "../../shared/Toasts";
import { ButtonVariant, Dialog } from "@merge-api/merge-javascript-shared";

type Props = {
  integrationMeta: Integration;
  isShown: boolean;
  onHide: () => void;
  integrationID: string;
  fetchMappingTestSuites: () => void;
};

export default function AddNewTestSuiteModal({
  integrationMeta,
  isShown,
  onHide,
  integrationID,
  fetchMappingTestSuites,
}: Props) {
  const categoriesForIntegration = integrationMeta.categories;
  const history = useHistory();

  const [isAdding, setIsAdding] = useState(false);
  const [testLinkedAccounts, setTestLinkedAccounts] = useState<LinkedAccount[]>();
  const [selectedLinkedAccountID, setSelectedLinkedAccountID] = useState<string>();
  const [selectedCategory, setSelectedCategory] = useState<string>(categoriesForIntegration[0]);

  useEffect(() => {
    fetchWithAuth({
      path: `/integrations/linked-accounts?is_test_account=True&is_sandbox_account=True&integration=${integrationID}`,
      method: "GET",
      onResponse: (response: { results: LinkedAccount[] }) => {
        setTestLinkedAccounts(response.results);
      },
      onError: () => showErrorToast("Unable to load linked accounts"),
    });
  }, [integrationID]);

  const filteredTestLinkedAccounts =
    testLinkedAccounts?.filter((linkedAccount) => linkedAccount.category === selectedCategory) ??
    [];

  const addNewTestSuite = useCallback(() => {
    if (selectedLinkedAccountID) {
      setIsAdding(true);
      fetchWithAuth({
        path: `/stallions/integrations/test-suite/create`,
        method: "POST",
        body: {
          linked_account_id: selectedLinkedAccountID,
        },
        onResponse: (testSuite: MappingTestSuiteMeta) => {
          navigateToIndividualTestSuite(history, integrationID, testSuite.id);
          fetchMappingTestSuites();
        },
        onError: () => {
          showErrorToast("Unable to create Mapping Test Suite");
          setIsAdding(false);
        },
      });
    }
  }, [history, integrationID, selectedLinkedAccountID]);

  return (
    <Dialog
      onClose={onHide}
      open={isShown}
      onPrimaryButtonClick={addNewTestSuite}
      primaryButtonLoading={isAdding}
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      primaryButtonDisabled={!selectedLinkedAccountID}
      primaryButtonText="Add test suite"
      onSecondaryButtonClick={onHide}
      secondaryButtonText="Cancel"
      title="Add test suite"
      variant="md"
    >
      {!testLinkedAccounts ? (
        <VersionControlSaveModal onHide={onHide} show={isShown} />
      ) : (
        <>
          {categoriesForIntegration.length > 1 && (
            <DropdownFormField
              placeholder="Select category"
              onChange={(e) => {
                setSelectedCategory(e.target.value);
              }}
              currentValue={selectedCategory}
              choices={
                categoriesForIntegration?.map((category) => ({
                  id: category,
                  name: category,
                })) ?? []
              }
            />
          )}
          <DropdownFormField
            title="Select Linked Account (sandbox accounts only)"
            placeholder=""
            disabled={!testLinkedAccounts || !selectedCategory}
            onChange={(e) => {
              setSelectedLinkedAccountID(e.target.value);
            }}
            currentValue={selectedLinkedAccountID}
            choices={
              filteredTestLinkedAccounts?.map(({ id, end_user }) => ({
                id,
                name: end_user.organization_name + " - " + id,
              })) ?? []
            }
          />
        </>
      )}
    </Dialog>
  );
}
